import {
  Button,
  Menu,
  MenuItem,
  NonIdealState,
  PopoverNext,
  Spinner,
} from "@blueprintjs/core";
import { useToaster } from "@package/ui-components";
import {
  GetScheduledEventDocument,
  GetScheduledEventsDocument,
  useDeleteScheduledEventMutation,
  useGetScheduledEventQuery,
} from "@package/ui-gql/index-data-access";
import { useCallback, useMemo, useState, type MouseEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  ITEM_GRID_PATH_EDIT,
  ITEM_GRID_PATH_EDIT_SCHEDULER_GROUP,
  SidebarEntryPath,
} from "../../../../constants/contants";
import { DashboardCard } from "../../../DashboardCard/DashboardCard";
import { SchedulerEventSummary } from "./SchedulerEventSummary";
import { useDeleteScheduledEventGroup } from "./use-delete-scheduled-event-group";
import { useSchedulerEntityNames } from "./use-scheduler-entity-names";

export type SchedulerPopoverOffset = { left: number; top: number };

type SchedulerEventPopoverProps = {
  eventId: string | null;
  offset: SchedulerPopoverOffset | null;
  onClose: () => void;
};

export function SchedulerEventPopover({
  eventId,
  offset,
  onClose,
}: SchedulerEventPopoverProps) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { toaster } = useToaster();
  const { deleteGroup, deleting: deletingGroup } =
    useDeleteScheduledEventGroup();

  const { data, error, loading } = useGetScheduledEventQuery({
    variables: { id: eventId ?? "" },
    skip: !eventId,
  });
  const [deleteEvent, { loading: deletingEvent }] =
    useDeleteScheduledEventMutation();
  const isDeleting = deletingEvent || deletingGroup;

  const event = data?.item;
  const eventActions = useMemo(
    () => [...(event?.startAction ?? []), ...(event?.endAction ?? [])],
    [event?.startAction, event?.endAction],
  );
  const names = useSchedulerEntityNames(eventActions);

  // `onClose` is the single place that clears both the selected event id and
  // the popover's screen position (owned by SchedulerCalendar) -- dispatching
  // the redux clear here too, on top of what onClose already does, was
  // redundant.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleEdit = useCallback(() => {
    if (!eventId) {
      return;
    }

    handleClose();
    navigate(
      `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_EDIT}/${eventId}${search}`,
    );
  }, [eventId, handleClose, navigate, search]);

  const handleEditSeries = useCallback(() => {
    if (!eventId) {
      return;
    }

    handleClose();
    navigate(
      `/${SidebarEntryPath.scheduler}/${ITEM_GRID_PATH_EDIT_SCHEDULER_GROUP}/${eventId}${search}`,
    );
  }, [eventId, handleClose, navigate, search]);

  const handleDelete = useCallback(() => {
    if (!eventId) {
      return;
    }

    deleteEvent({
      variables: { id: eventId },
      refetchQueries: [GetScheduledEventDocument, GetScheduledEventsDocument],
      onCompleted: () => {
        toaster?.show({
          icon: "tick",
          intent: "success",
          message: "Deleted event",
          timeout: 3000,
        });
        handleClose();
      },
      // Without this, a GraphQL error (permissions, validation, network)
      // failed silently -- no toast, no closed popover, nothing -- which is
      // indistinguishable from "the button doesn't do anything".
      onError: (mutationError) => {
        console.error("[SchedulerEventPopover]: Error: ", mutationError);
        toaster?.show({
          icon: "warning-sign",
          intent: "danger",
          message: "Failed deleting event",
          timeout: 3000,
        });
      },
    });
  }, [deleteEvent, eventId, handleClose, toaster]);

  const handleDeleteSeries = useCallback(async () => {
    if (!event?.groupId) {
      return;
    }

    toaster?.show({
      icon: "trash",
      intent: "none",
      message: "Deleting all recurring events",
      timeout: 3000,
    });

    try {
      await deleteGroup(event.groupId);
      toaster?.show({
        icon: "tick",
        intent: "success",
        message: "Deleted all recurring events",
        timeout: 3000,
      });
      handleClose();
    } catch (e: unknown) {
      console.error("[SchedulerEventPopover]: Error: ", e);
      toaster?.show({
        icon: "warning-sign",
        intent: "danger",
        message: "Failed deleting all recurring events",
        timeout: 3000,
      });
    }
  }, [deleteGroup, event, handleClose, toaster]);

  return (
    // A point-anchored popover via `renderTarget` -- a documented PopoverNext
    // API (the same mechanism Blueprint's own Select uses internally), not a
    // hand-rolled hack. `ContextMenuPopover` looked like the more obvious
    // Blueprint primitive for this, but it forces its underlying PopoverNext
    // to remount (it keys that element off the target position) every time
    // the position changes -- fine for its intended one-shot "right-click,
    // pick one thing, done" use, but this panel stays open and re-targets
    // itself on every click while the user browses between events, and
    // remounting mid-open on every single click was leaving the popover (and
    // the in-flight event query tied to it) in a stuck state that only a
    // full unmount of the page could clear. A stable target that just moves
    // avoids that entirely: same PopoverNext instance the whole time, only
    // its position and content change. The popover also closes itself if the
    // calendar scrolls or the window resizes underneath it, since the
    // position was captured once at click time and never re-measured.
    <PopoverNext
      canEscapeKeyClose
      content={
        <DashboardCard className="w-80 p-3 shadow-lg">
          {loading && <Spinner size={24} />}
          {!loading && (error || !event) && (
            <NonIdealState
              description="This event may have been deleted."
              icon="warning-sign"
              title="Couldn't load event"
            />
          )}
          {!loading && !error && event && (
            <>
              <div className="flex items-start justify-between gap-2">
                <h4 className="m-0 min-w-0 truncate text-sm font-semibold text-brand-ink">
                  {event.name}
                </h4>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    disabled={isDeleting}
                    icon="edit"
                    minimal
                    onClick={handleEdit}
                    small
                    title="Edit"
                  />
                  {event.groupId ? (
                    <SchedulerDeleteMenu
                      disabled={isDeleting}
                      onDeleteSeries={handleDeleteSeries}
                      onDeleteThis={handleDelete}
                    />
                  ) : (
                    <Button
                      disabled={isDeleting}
                      icon="trash"
                      intent="danger"
                      loading={deletingEvent}
                      minimal
                      onClick={handleDelete}
                      small
                      title="Delete"
                    />
                  )}
                  <Button
                    icon="cross"
                    minimal
                    onClick={handleClose}
                    small
                    title="Close"
                  />
                </div>
              </div>
              <SchedulerEventSummary
                event={event}
                names={names}
                onEditSeries={handleEditSeries}
              />
            </>
          )}
        </DashboardCard>
      }
      isOpen={!!offset && !!eventId}
      onClose={handleClose}
      placement="right-start"
      renderTarget={({ isOpen, ref, ...targetProps }) => (
        <span
          {...targetProps}
          aria-expanded={isOpen}
          ref={ref}
          style={{
            position: "fixed",
            left: offset?.left ?? 0,
            top: offset?.top ?? 0,
            width: 0,
            height: 0,
            pointerEvents: "none",
          }}
        />
      )}
    />
  );
}

function SchedulerDeleteMenu({
  disabled,
  onDeleteSeries,
  onDeleteThis,
}: {
  disabled?: boolean;
  onDeleteThis: () => void;
  onDeleteSeries: () => void;
}) {
  // This dropdown is controlled (not left to Popover's default
  // click-to-dismiss behavior) because MenuItem's default dismiss marks
  // itself with a CSS class that *any* ancestor Popover's click handler
  // checks for -- including the outer event-detail popover this menu is
  // nested inside. Left uncontrolled, picking either option would close
  // both this dropdown *and* the whole detail panel in the same click,
  // before the mutation had even started. `shouldDismissPopover={false}`
  // opts both items out of that shared mechanism, and stopPropagation
  // belt-and-suspenders against the outer popover's separate
  // click-outside-of-my-content detection.
  const [isOpen, setIsOpen] = useState(false);

  const handleDeleteThis = (event: MouseEvent) => {
    event.stopPropagation();
    setIsOpen(false);
    onDeleteThis();
  };
  const handleDeleteSeriesClick = (event: MouseEvent) => {
    event.stopPropagation();
    setIsOpen(false);
    onDeleteSeries();
  };

  return (
    <PopoverNext
      content={
        <Menu>
          <MenuItem
            icon="trash"
            onClick={handleDeleteThis}
            shouldDismissPopover={false}
            text="This event"
          />
          <MenuItem
            icon="multi-select"
            intent="danger"
            onClick={handleDeleteSeriesClick}
            shouldDismissPopover={false}
            text="All events in series"
          />
        </Menu>
      }
      disabled={disabled}
      isOpen={isOpen}
      onInteraction={setIsOpen}
      placement="bottom-end"
    >
      <Button
        disabled={disabled}
        icon="trash"
        intent="danger"
        minimal
        small
        title="Delete"
      />
    </PopoverNext>
  );
}
